import React, { useEffect, createContext, useState, useMemo } from 'react';
import apiValidateToken from '../api/apiValidateToken';
import { apiCheckBjornUser } from '../api/apiBjornIntegration';
import { trackConversion, trackSignIn } from '../utils/tracking';

export const UserSettingsContext = createContext();

export const UserSettingsProvider = ({ children }) => {
    const [settings, setSettings] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [initializing, setInitializing] = useState(true);
    const [bjornUser, setBjornUser] = useState(null); // Bjorn user info if email matches
    const [theme, setThemeState] = useState(() => {
        return localStorage.getItem('captions_theme') || 'light';
    });
    const [translateMode, setTranslateModeState] = useState(() => {
        return localStorage.getItem('captions_translate_mode') === 'true';
    });

    useEffect(() => {
        let isMounted = true;

        const clearAuthState = () => {
            localStorage.removeItem('captions_token');
            localStorage.removeItem('captions_user_id');

            if (!isMounted) return;
            setIsLoggedIn(false);
            setSettings(null);
            setBjornUser(null);
        };

        const validateAndLoadUser = async () => {
            const result = await apiValidateToken();

            if (!result.valid) {
                clearAuthState();
                return false;
            }

            if (!isMounted) return true;

            setIsLoggedIn(true);
            setSettings({
                user_id: result.user_id,
                email: result.email,
                first_name: result.first_name,
            });

            const bjornResult = await apiCheckBjornUser();
            if (!isMounted) return true;

            if (bjornResult.exists) {
                setBjornUser({
                    bjorn_user_id: bjornResult.bjorn_user_id,
                    first_name: bjornResult.first_name,
                });
            } else {
                setBjornUser(null);
            }

            return true;
        };

        const initializeAuth = async () => {
            try {
                // Check for OAuth callback params first
                const params = new URLSearchParams(window.location.search);
                const token = params.get('token');
                const userId = params.get('userId');
                const error = params.get('error');

                // Handle OAuth error
                if (error === 'oauth_failed') {
                    window.history.replaceState(
                        {},
                        document.title,
                        window.location.pathname
                    );
                    clearAuthState();
                    return;
                }

                // Handle successful OAuth callback
                if (token && userId) {
                    localStorage.setItem('captions_token', token);
                    localStorage.setItem('captions_user_id', userId);

                    const isNewUser = params.get('isNewUser') === 'true';
                    trackSignIn();
                    if (isNewUser) {
                        trackConversion();
                    }

                    // Always clean up OAuth query params
                    window.history.replaceState(
                        {},
                        document.title,
                        window.location.pathname
                    );

                    await validateAndLoadUser();
                    return;
                }

                // Check for existing token
                const existingToken = localStorage.getItem('captions_token');
                if (existingToken) {
                    await validateAndLoadUser();
                    return;
                }

                if (!isMounted) return;
                setIsLoggedIn(false);
                setSettings(null);
                setBjornUser(null);
            } catch {
                clearAuthState();
            } finally {
                if (isMounted) {
                    setInitializing(false);
                }
            }
        };

        initializeAuth();

        return () => {
            isMounted = false;
        };
    }, []);

    const logout = () => {
        localStorage.removeItem('captions_token');
        localStorage.removeItem('captions_user_id');
        setIsLoggedIn(false);
        setSettings(null);
        setBjornUser(null);
    };

    const setTheme = (newTheme) => {
        setThemeState(newTheme);
        localStorage.setItem('captions_theme', newTheme);
    };

    const setTranslateMode = (enabled) => {
        setTranslateModeState(enabled);
        localStorage.setItem('captions_translate_mode', enabled.toString());
    };

    const contextValue = useMemo(
        () => ({
            settings,
            setSettings,
            isLoggedIn,
            setIsLoggedIn,
            logout,
            theme,
            setTheme,
            translateMode,
            setTranslateMode,
            bjornUser,
            setBjornUser,
        }),
        [settings, isLoggedIn, theme, translateMode, bjornUser]
    );

    return (
        <UserSettingsContext.Provider value={contextValue}>
            {initializing ? null : children}
        </UserSettingsContext.Provider>
    );
};
